/**
 * 1.68 (196) — each waiter's numbers for a period (`waiter_stats`, spec G10–G12): checks paid, guests, sales (what the
 * sales came to less every return made from them — never „cash handed in", a sale may hold a ნისია part) and the
 * service (its lines' net value less the service refunded). A later return lowers the period of the sale it returns.
 * The row with no waiter is the till's own bills („სალარო").
 */
import type { Shift } from './mock-data'
import { toTetri } from './money-round'
import { addDays, businessDay, businessDayRangeUtc } from './business-day'

export type WaiterStatRow = {
  waiterId: string | null
  waiterName: string | null
  checks: number
  guests: number
  sales: number
  service: number
}

const num = (v: unknown) => (v == null ? 0 : Number(v))

export function mapWaiterStats(raw: unknown): WaiterStatRow[] {
  if (!Array.isArray(raw)) return []
  return raw.map((r: any) => ({
    waiterId: r?.waiter_id ?? null, waiterName: r?.waiter_name ?? null,
    checks: num(r?.checks), guests: num(r?.guests), sales: num(r?.sales), service: num(r?.service),
  }))
}

/** The sum of the rows, money to the tetri. */
export function statsTotal(rows: readonly WaiterStatRow[]): WaiterStatRow {
  let checks = 0, guests = 0, sales = 0, service = 0
  for (const r of rows) {
    checks += r.checks
    guests += r.guests
    sales += toTetri(r.sales)
    service += toTetri(r.service)
  }
  return { waiterId: null, waiterName: null, checks, guests, sales: sales / 100, service: service / 100 }
}

/** „ეს ცვლა": the branch's open shift (its opening to now), else its last closed one (opening to closing). */
export function shiftWindow(shifts: readonly Shift[], branchId: string, now: Date): { from: string; to: string } | null {
  const mine = shifts.filter((s) => (s.branch_id ?? null) === branchId)
  const open = mine.filter((s) => s.status === 'open').sort((a, b) => b.opened_at.localeCompare(a.opened_at))[0]
  if (open) return { from: open.opened_at, to: now.toISOString() }
  const last = mine.filter((s) => s.status === 'closed' && s.closed_at)
    .sort((a, b) => (b.closed_at ?? '').localeCompare(a.closed_at ?? ''))[0]
  return last ? { from: last.opened_at, to: last.closed_at as string } : null
}

export type StatsPeriod = 'shift' | 'today' | 'week' | 'month'

/**
 * The owner's periods (Staff → „მიმტანები", spec G12): this shift (`shiftWindow`), else today · this week (from Monday)
 * · this month (from the 1st) — whole business days of the shop (`dayStartHour`, Tbilisi), up to now.
 */
export function statsPeriod(kind: StatsPeriod, shifts: readonly Shift[], branchId: string, now: Date, startHour: number): { from: string; to: string } | null {
  if (kind === 'shift') return shiftWindow(shifts, branchId, now)
  const day = businessDay(now, startHour)
  const first = kind === 'today' ? day
    : kind === 'week' ? addDays(day, -((new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7))
      : `${day.slice(0, 8)}01`
  return { from: businessDayRangeUtc(first, startHour).from, to: now.toISOString() }
}
